import React, { useState, useEffect, useRef } from "react";
import logo from "../../icon/logo.png";
import supabase from "../../supabase";


const STORAGE_KEY = "tf_lang";

const LANGUAGES = [
  { code:"en",    label:"English",    flag:"🇬🇧" },
  { code:"fr",    label:"Français",   flag:"🇫🇷" },
  { code:"es",    label:"Español",    flag:"🇪🇸" },
  { code:"de",    label:"Deutsch",    flag:"🇩🇪" },
  { code:"pt",    label:"Português",  flag:"🇧🇷" },
  { code:"ar",    label:"العربية",    flag:"🇸🇦" },
  { code:"zh-CN", label:"中文",        flag:"🇨🇳" },
  { code:"ru",    label:"Русский",    flag:"🇷🇺" },
  { code:"ja",    label:"日本語",      flag:"🇯🇵" },
  { code:"ko",    label:"한국어",      flag:"🇰🇷" },
  { code:"hi",    label:"हिन्दी",     flag:"🇮🇳" },
  { code:"sw",    label:"Kiswahili",  flag:"🇰🇪" },
  { code:"ha",    label:"Hausa",      flag:"🇳🇬" },
  { code:"yo",    label:"Yorùbá",     flag:"🇳🇬" },
  { code:"ig",    label:"Igbo",       flag:"🇳🇬" },
  { code:"am",    label:"አማርኛ",      flag:"🇪🇹" },
  { code:"af",    label:"Afrikaans",  flag:"🇿🇦" },
  { code:"zu",    label:"isiZulu",    flag:"🇿🇦" },
  { code:"pl",    label:"Polski",     flag:"🇵🇱" },
  { code:"fi",    label:"Suomi",      flag:"🇫🇮" },
  { code:"cs",    label:"Čeština",    flag:"🇨🇿" },
  { code:"ga",    label:"Gaeilge",    flag:"🇮🇪" },
  { code:"it",    label:"Italiano",   flag:"🇮🇹" },
  { code:"nl",    label:"Nederlands", flag:"🇳🇱" },
  { code:"sv",    label:"Svenska",    flag:"🇸🇪" },
  { code:"no",    label:"Norsk",      flag:"🇳🇴" },
  { code:"da",    label:"Dansk",      flag:"🇩🇰" },
  { code:"el",    label:"Ελληνικά",   flag:"🇬🇷" },
  { code:"uk",    label:"Українська", flag:"🇺🇦" },
  { code:"tr",    label:"Türkçe",     flag:"🇹🇷" },
  { code:"vi",    label:"Tiếng Việt", flag:"🇻🇳" },
  { code:"id",    label:"Bahasa Indonesia", flag:"🇮🇩" },
  { code:"th",    label:"ไทย",        flag:"🇹🇭" },
  { code:"he",    label:"עברית",      flag:"🇮🇱" },
  { code:"ur",    label:"اردو",       flag:"🇵🇰" },
  { code:"fa",    label:"فارسی",      flag:"🇮🇷" },
  { code:"bn",    label:"বাংলা",      flag:"🇧🇩" },
];

// Initialisation callback required by the Google Translate script
window.googleTranslateElementInit = () => {
  new window.google.translate.TranslateElement(
    { pageLanguage:"en", autoDisplay:false },
    "tf-translate-el"
  );
};

const applyGoogleTranslate = (langCode) => {
  // Set the cookie Google Translate reads on every page load
  const val = langCode === "en" ? "/en/en" : `/en/${langCode}`;
  document.cookie = `googtrans=${val}; path=/`;
  localStorage.setItem(STORAGE_KEY, langCode);
  // Trigger the hidden select that Google Translate exposes
  const sel = document.querySelector(".goog-te-combo");
  if (sel) {
    sel.value = langCode;
    sel.dispatchEvent(new Event("change"));
  } else {
    // Script not ready yet — reload with cookie set will apply it
    window.location.reload();
  }
};

const useGoogleTranslate = () => {
  useEffect(() => {
    // Restore saved language on mount
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && saved !== "en") {
      document.cookie = `googtrans=/en/${saved}; path=/`;
    }
    // Inject script once
    if (!document.getElementById("gt-script")) {
      const s = document.createElement("script");
      s.id  = "gt-script";
      s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);
};

const useChatwayWidget = () => {
  useEffect(() => {
    if (document.getElementById("chatway-widget-script")) return;
    const script = document.createElement("script");
    script.id = "chatway-widget-script";
    script.src = "https://cdn.chatway.app/widget.js?id=vK7FysdMzgk1";
    script.async = true;
    document.body.appendChild(script);
  }, []);
};

const ChevronDown = () => (
  <svg className="tf-chevron" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);

const LanguagePicker = () => {
  const [open,    setOpen]    = useState(false);
  const [current, setCurrent] = useState(
    () => localStorage.getItem(STORAGE_KEY) || "en"
  );
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (code) => {
    setCurrent(code);
    setOpen(false);
    applyGoogleTranslate(code);
  };

  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <div className="tf-lang-wrap" ref={wrapRef}>
      {/* Hidden Google Translate mount point */}
      <div id="tf-translate-el" style={{display:"none"}}/>

      <button className={`tf-lang-btn${open?" open":""}`} onClick={() => setOpen(o => !o)}>
        <GlobeIcon/>
        <span>{active.flag}</span>
        <span>{active.label}</span>
        <ChevronDown/>
      </button>

      {open && (
        <div className="tf-lang-menu">
          {LANGUAGES.map(lang => (
            <div
              key={lang.code}
              className={`tf-lang-opt${lang.code===current?" active":""}`}
              onClick={() => select(lang.code)}
            >
              <span className="tf-lang-flag">{lang.flag}</span>
              <span>{lang.label}</span>
              {lang.code === current && (
                <span style={{marginLeft:"auto", fontSize:"11px", opacity:.7}}>✓</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   DESIGN TOKENS  (match Optionstock landing page —
   "Institutional" navy & emerald, light-cool)
───────────────────────────────────────────── */
const C = {
  bg:         "#F3F6FA",
  surface:    "#FFFFFF",
  surfaceAlt: "#E9EFF5",
  border:     "#D8E2EC",
  borderFocus:"#0E7C5A60",
  borderHover:"#B9C9DA",
  accent:     "#0E7C5A",
  accentDim:  "rgba(14,124,90,0.08)",
  accentGlow: "rgba(14,124,90,0.18)",
  text:       "#16283F",
  textMid:    "#4C6178",
  textDim:    "#8C9BAC",
  red:        "#C0392B",
  green:      "#16915F",
  blue:       "#2F6690",
};

/* ─────────────────────────────────────────────
   COUNTRIES (full list with dial codes)
───────────────────────────────────────────── */
const COUNTRIES = [
  { code:"AF", name:"Afghanistan", dial:"+93", currency:"AFN" },
  { code:"AL", name:"Albania", dial:"+355", currency:"ALL" },
  { code:"DZ", name:"Algeria", dial:"+213", currency:"DZD" },
  { code:"AD", name:"Andorra", dial:"+376", currency:"EUR" },
  { code:"AO", name:"Angola", dial:"+244", currency:"AOA" },
  { code:"AG", name:"Antigua and Barbuda", dial:"+1-268", currency:"XCD" },
  { code:"AR", name:"Argentina", dial:"+54", currency:"ARS" },
  { code:"AM", name:"Armenia", dial:"+374", currency:"AMD" },
  { code:"AU", name:"Australia", dial:"+61", currency:"AUD" },
  { code:"AT", name:"Austria", dial:"+43", currency:"EUR" },
  { code:"AZ", name:"Azerbaijan", dial:"+994", currency:"AZN" },
  { code:"BS", name:"Bahamas", dial:"+1-242", currency:"BSD" },
  { code:"BH", name:"Bahrain", dial:"+973", currency:"BHD" },
  { code:"BD", name:"Bangladesh", dial:"+880", currency:"BDT" },
  { code:"BB", name:"Barbados", dial:"+1-246", currency:"BBD" },
  { code:"BY", name:"Belarus", dial:"+375", currency:"BYN" },
  { code:"BE", name:"Belgium", dial:"+32", currency:"EUR" },
  { code:"BZ", name:"Belize", dial:"+501", currency:"BZD" },
  { code:"BJ", name:"Benin", dial:"+229", currency:"XOF" },
  { code:"BT", name:"Bhutan", dial:"+975", currency:"BTN" },
  { code:"BO", name:"Bolivia", dial:"+591", currency:"BOB" },
  { code:"BA", name:"Bosnia and Herzegovina", dial:"+387", currency:"BAM" },
  { code:"BW", name:"Botswana", dial:"+267", currency:"BWP" },
  { code:"BR", name:"Brazil", dial:"+55", currency:"BRL" },
  { code:"BN", name:"Brunei", dial:"+673", currency:"BND" },
  { code:"BG", name:"Bulgaria", dial:"+359", currency:"BGN" },
  { code:"BF", name:"Burkina Faso", dial:"+226", currency:"XOF" },
  { code:"BI", name:"Burundi", dial:"+257", currency:"BIF" },
  { code:"CV", name:"Cabo Verde", dial:"+238", currency:"CVE" },
  { code:"KH", name:"Cambodia", dial:"+855", currency:"KHR" },
  { code:"CM", name:"Cameroon", dial:"+237", currency:"XAF" },
  { code:"CA", name:"Canada", dial:"+1", currency:"CAD" },
  { code:"CF", name:"Central African Republic", dial:"+236", currency:"XAF" },
  { code:"TD", name:"Chad", dial:"+235", currency:"XAF" },
  { code:"CL", name:"Chile", dial:"+56", currency:"CLP" },
  { code:"CN", name:"China", dial:"+86", currency:"CNY" },
  { code:"CO", name:"Colombia", dial:"+57", currency:"COP" },
  { code:"KM", name:"Comoros", dial:"+269", currency:"KMF" },
  { code:"CG", name:"Congo", dial:"+242", currency:"XAF" },
  { code:"CD", name:"Congo (DRC)", dial:"+243", currency:"CDF" },
  { code:"CR", name:"Costa Rica", dial:"+506", currency:"CRC" },
  { code:"CI", name:"Cote d'Ivoire", dial:"+225", currency:"XOF" },
  { code:"HR", name:"Croatia", dial:"+385", currency:"EUR" },
  { code:"CU", name:"Cuba", dial:"+53", currency:"CUP" },
  { code:"CY", name:"Cyprus", dial:"+357", currency:"EUR" },
  { code:"CZ", name:"Czech Republic", dial:"+420", currency:"CZK" },
  { code:"DK", name:"Denmark", dial:"+45", currency:"DKK" },
  { code:"DJ", name:"Djibouti", dial:"+253", currency:"DJF" },
  { code:"DM", name:"Dominica", dial:"+1-767", currency:"XCD" },
  { code:"DO", name:"Dominican Republic", dial:"+1-809", currency:"DOP" },
  { code:"EC", name:"Ecuador", dial:"+593", currency:"USD" },
  { code:"EG", name:"Egypt", dial:"+20", currency:"EGP" },
  { code:"SV", name:"El Salvador", dial:"+503", currency:"USD" },
  { code:"GQ", name:"Equatorial Guinea", dial:"+240", currency:"XAF" },
  { code:"ER", name:"Eritrea", dial:"+291", currency:"ERN" },
  { code:"EE", name:"Estonia", dial:"+372", currency:"EUR" },
  { code:"SZ", name:"Eswatini", dial:"+268", currency:"SZL" },
  { code:"ET", name:"Ethiopia", dial:"+251", currency:"ETB" },
  { code:"FJ", name:"Fiji", dial:"+679", currency:"FJD" },
  { code:"FI", name:"Finland", dial:"+358", currency:"EUR" },
  { code:"FR", name:"France", dial:"+33", currency:"EUR" },
  { code:"GA", name:"Gabon", dial:"+241", currency:"XAF" },
  { code:"GM", name:"Gambia", dial:"+220", currency:"GMD" },
  { code:"GE", name:"Georgia", dial:"+995", currency:"GEL" },
  { code:"DE", name:"Germany", dial:"+49", currency:"EUR" },
  { code:"GH", name:"Ghana", dial:"+233", currency:"GHS" },
  { code:"GR", name:"Greece", dial:"+30", currency:"EUR" },
  { code:"GD", name:"Grenada", dial:"+1-473", currency:"XCD" },
  { code:"GT", name:"Guatemala", dial:"+502", currency:"GTQ" },
  { code:"GN", name:"Guinea", dial:"+224", currency:"GNF" },
  { code:"GW", name:"Guinea-Bissau", dial:"+245", currency:"XOF" },
  { code:"GY", name:"Guyana", dial:"+592", currency:"GYD" },
  { code:"HT", name:"Haiti", dial:"+509", currency:"HTG" },
  { code:"HN", name:"Honduras", dial:"+504", currency:"HNL" },
  { code:"HU", name:"Hungary", dial:"+36", currency:"HUF" },
  { code:"IS", name:"Iceland", dial:"+354", currency:"ISK" },
  { code:"IN", name:"India", dial:"+91", currency:"INR" },
  { code:"ID", name:"Indonesia", dial:"+62", currency:"IDR" },
  { code:"IR", name:"Iran", dial:"+98", currency:"IRR" },
  { code:"IQ", name:"Iraq", dial:"+964", currency:"IQD" },
  { code:"IE", name:"Ireland", dial:"+353", currency:"EUR" },
  { code:"IL", name:"Israel", dial:"+972", currency:"ILS" },
  { code:"IT", name:"Italy", dial:"+39", currency:"EUR" },
  { code:"JM", name:"Jamaica", dial:"+1-876", currency:"JMD" },
  { code:"JP", name:"Japan", dial:"+81", currency:"JPY" },
  { code:"JO", name:"Jordan", dial:"+962", currency:"JOD" },
  { code:"KZ", name:"Kazakhstan", dial:"+7", currency:"KZT" },
  { code:"KE", name:"Kenya", dial:"+254", currency:"KES" },
  { code:"KI", name:"Kiribati", dial:"+686", currency:"AUD" },
  { code:"KW", name:"Kuwait", dial:"+965", currency:"KWD" },
  { code:"KG", name:"Kyrgyzstan", dial:"+996", currency:"KGS" },
  { code:"LA", name:"Laos", dial:"+856", currency:"LAK" },
  { code:"LV", name:"Latvia", dial:"+371", currency:"EUR" },
  { code:"LB", name:"Lebanon", dial:"+961", currency:"LBP" },
  { code:"LS", name:"Lesotho", dial:"+266", currency:"LSL" },
  { code:"LR", name:"Liberia", dial:"+231", currency:"LRD" },
  { code:"LY", name:"Libya", dial:"+218", currency:"LYD" },
  { code:"LI", name:"Liechtenstein", dial:"+423", currency:"CHF" },
  { code:"LT", name:"Lithuania", dial:"+370", currency:"EUR" },
  { code:"LU", name:"Luxembourg", dial:"+352", currency:"EUR" },
  { code:"MG", name:"Madagascar", dial:"+261", currency:"MGA" },
  { code:"MW", name:"Malawi", dial:"+265", currency:"MWK" },
  { code:"MY", name:"Malaysia", dial:"+60", currency:"MYR" },
  { code:"MV", name:"Maldives", dial:"+960", currency:"MVR" },
  { code:"ML", name:"Mali", dial:"+223", currency:"XOF" },
  { code:"MT", name:"Malta", dial:"+356", currency:"EUR" },
  { code:"MH", name:"Marshall Islands", dial:"+692", currency:"USD" },
  { code:"MR", name:"Mauritania", dial:"+222", currency:"MRU" },
  { code:"MU", name:"Mauritius", dial:"+230", currency:"MUR" },
  { code:"MX", name:"Mexico", dial:"+52", currency:"MXN" },
  { code:"FM", name:"Micronesia", dial:"+691", currency:"USD" },
  { code:"MD", name:"Moldova", dial:"+373", currency:"MDL" },
  { code:"MC", name:"Monaco", dial:"+377", currency:"EUR" },
  { code:"MN", name:"Mongolia", dial:"+976", currency:"MNT" },
  { code:"ME", name:"Montenegro", dial:"+382", currency:"EUR" },
  { code:"MA", name:"Morocco", dial:"+212", currency:"MAD" },
  { code:"MZ", name:"Mozambique", dial:"+258", currency:"MZN" },
  { code:"MM", name:"Myanmar", dial:"+95", currency:"MMK" },
  { code:"NA", name:"Namibia", dial:"+264", currency:"NAD" },
  { code:"NR", name:"Nauru", dial:"+674", currency:"AUD" },
  { code:"NP", name:"Nepal", dial:"+977", currency:"NPR" },
  { code:"NL", name:"Netherlands", dial:"+31", currency:"EUR" },
  { code:"NZ", name:"New Zealand", dial:"+64", currency:"NZD" },
  { code:"NI", name:"Nicaragua", dial:"+505", currency:"NIO" },
  { code:"NE", name:"Niger", dial:"+227", currency:"XOF" },
  { code:"NG", name:"Nigeria", dial:"+234", currency:"NGN" },
  { code:"MK", name:"North Macedonia", dial:"+389", currency:"MKD" },
  { code:"NO", name:"Norway", dial:"+47", currency:"NOK" },
  { code:"OM", name:"Oman", dial:"+968", currency:"OMR" },
  { code:"PK", name:"Pakistan", dial:"+92", currency:"PKR" },
  { code:"PW", name:"Palau", dial:"+680", currency:"USD" },
  { code:"PA", name:"Panama", dial:"+507", currency:"PAB" },
  { code:"PG", name:"Papua New Guinea", dial:"+675", currency:"PGK" },
  { code:"PY", name:"Paraguay", dial:"+595", currency:"PYG" },
  { code:"PE", name:"Peru", dial:"+51", currency:"PEN" },
  { code:"PH", name:"Philippines", dial:"+63", currency:"PHP" },
  { code:"PL", name:"Poland", dial:"+48", currency:"PLN" },
  { code:"PT", name:"Portugal", dial:"+351", currency:"EUR" },
  { code:"QA", name:"Qatar", dial:"+974", currency:"QAR" },
  { code:"RO", name:"Romania", dial:"+40", currency:"RON" },
  { code:"RU", name:"Russia", dial:"+7", currency:"RUB" },
  { code:"RW", name:"Rwanda", dial:"+250", currency:"RWF" },
  { code:"KN", name:"Saint Kitts and Nevis", dial:"+1-869", currency:"XCD" },
  { code:"LC", name:"Saint Lucia", dial:"+1-758", currency:"XCD" },
  { code:"VC", name:"Saint Vincent and the Grenadines", dial:"+1-784", currency:"XCD" },
  { code:"WS", name:"Samoa", dial:"+685", currency:"WST" },
  { code:"SM", name:"San Marino", dial:"+378", currency:"EUR" },
  { code:"ST", name:"Sao Tome and Principe", dial:"+239", currency:"STN" },
  { code:"SA", name:"Saudi Arabia", dial:"+966", currency:"SAR" },
  { code:"SN", name:"Senegal", dial:"+221", currency:"XOF" },
  { code:"RS", name:"Serbia", dial:"+381", currency:"RSD" },
  { code:"SC", name:"Seychelles", dial:"+248", currency:"SCR" },
  { code:"SL", name:"Sierra Leone", dial:"+232", currency:"SLL" },
  { code:"SG", name:"Singapore", dial:"+65", currency:"SGD" },
  { code:"SK", name:"Slovakia", dial:"+421", currency:"EUR" },
  { code:"SI", name:"Slovenia", dial:"+386", currency:"EUR" },
  { code:"SB", name:"Solomon Islands", dial:"+677", currency:"SBD" },
  { code:"SO", name:"Somalia", dial:"+252", currency:"SOS" },
  { code:"ZA", name:"South Africa", dial:"+27", currency:"ZAR" },
  { code:"SS", name:"South Sudan", dial:"+211", currency:"SSP" },
  { code:"ES", name:"Spain", dial:"+34", currency:"EUR" },
  { code:"LK", name:"Sri Lanka", dial:"+94", currency:"LKR" },
  { code:"SD", name:"Sudan", dial:"+249", currency:"SDG" },
  { code:"SR", name:"Suriname", dial:"+597", currency:"SRD" },
  { code:"SE", name:"Sweden", dial:"+46", currency:"SEK" },
  { code:"CH", name:"Switzerland", dial:"+41", currency:"CHF" },
  { code:"SY", name:"Syria", dial:"+963", currency:"SYP" },
  { code:"TW", name:"Taiwan", dial:"+886", currency:"TWD" },
  { code:"TJ", name:"Tajikistan", dial:"+992", currency:"TJS" },
  { code:"TZ", name:"Tanzania", dial:"+255", currency:"TZS" },
  { code:"TH", name:"Thailand", dial:"+66", currency:"THB" },
  { code:"TL", name:"Timor-Leste", dial:"+670", currency:"USD" },
  { code:"TG", name:"Togo", dial:"+228", currency:"XOF" },
  { code:"TO", name:"Tonga", dial:"+676", currency:"TOP" },
  { code:"TT", name:"Trinidad and Tobago", dial:"+1-868", currency:"TTD" },
  { code:"TN", name:"Tunisia", dial:"+216", currency:"TND" },
  { code:"TR", name:"Turkey", dial:"+90", currency:"TRY" },
  { code:"TM", name:"Turkmenistan", dial:"+993", currency:"TMT" },
  { code:"TV", name:"Tuvalu", dial:"+688", currency:"AUD" },
  { code:"UG", name:"Uganda", dial:"+256", currency:"UGX" },
  { code:"UA", name:"Ukraine", dial:"+380", currency:"UAH" },
  { code:"AE", name:"United Arab Emirates", dial:"+971", currency:"AED" },
  { code:"GB", name:"United Kingdom", dial:"+44", currency:"GBP" },
  { code:"US", name:"United States", dial:"+1", currency:"USD" },
  { code:"UY", name:"Uruguay", dial:"+598", currency:"UYU" },
  { code:"UZ", name:"Uzbekistan", dial:"+998", currency:"UZS" },
  { code:"VU", name:"Vanuatu", dial:"+678", currency:"VUV" },
  { code:"VE", name:"Venezuela", dial:"+58", currency:"VES" },
  { code:"VN", name:"Vietnam", dial:"+84", currency:"VND" },
  { code:"YE", name:"Yemen", dial:"+967", currency:"YER" },
  { code:"ZM", name:"Zambia", dial:"+260", currency:"ZMW" },
  { code:"ZW", name:"Zimbabwe", dial:"+263", currency:"ZWL" },
];

const CURRENCIES = [
  { code:"USD", name:"US Dollar", symbol:"$" },
  { code:"EUR", name:"Euro", symbol:"€" },
  { code:"GBP", name:"British Pound", symbol:"£" },
  { code:"JPY", name:"Japanese Yen", symbol:"¥" },
  { code:"CHF", name:"Swiss Franc", symbol:"Fr" },
  { code:"CAD", name:"Canadian Dollar", symbol:"C$" },
  { code:"AUD", name:"Australian Dollar", symbol:"A$" },
  { code:"NZD", name:"New Zealand Dollar", symbol:"NZ$" },
  { code:"CNY", name:"Chinese Yuan", symbol:"¥" },
  { code:"HKD", name:"Hong Kong Dollar", symbol:"HK$" },
  { code:"SGD", name:"Singapore Dollar", symbol:"S$" },
  { code:"SEK", name:"Swedish Krona", symbol:"kr" },
  { code:"NOK", name:"Norwegian Krone", symbol:"kr" },
  { code:"DKK", name:"Danish Krone", symbol:"kr" },
  { code:"INR", name:"Indian Rupee", symbol:"₹" },
  { code:"BRL", name:"Brazilian Real", symbol:"R$" },
  { code:"MXN", name:"Mexican Peso", symbol:"MX$" },
  { code:"ZAR", name:"South African Rand", symbol:"R" },
  { code:"NGN", name:"Nigerian Naira", symbol:"₦" },
  { code:"KES", name:"Kenyan Shilling", symbol:"KSh" },
  { code:"GHS", name:"Ghanaian Cedi", symbol:"₵" },
  { code:"EGP", name:"Egyptian Pound", symbol:"E£" },
  { code:"MAD", name:"Moroccan Dirham", symbol:"د.م." },
  { code:"AED", name:"UAE Dirham", symbol:"د.إ" },
  { code:"SAR", name:"Saudi Riyal", symbol:"ر.س" },
  { code:"QAR", name:"Qatari Riyal", symbol:"ر.ق" },
  { code:"KWD", name:"Kuwaiti Dinar", symbol:"د.ك" },
  { code:"TRY", name:"Turkish Lira", symbol:"₺" },
  { code:"RUB", name:"Russian Ruble", symbol:"₽" },
  { code:"PLN", name:"Polish Zloty", symbol:"zł" },
  { code:"CZK", name:"Czech Koruna", symbol:"Kč" },
  { code:"HUF", name:"Hungarian Forint", symbol:"Ft" },
  { code:"RON", name:"Romanian Leu", symbol:"lei" },
  { code:"ILS", name:"Israeli Shekel", symbol:"₪" },
  { code:"PHP", name:"Philippine Peso", symbol:"₱" },
  { code:"THB", name:"Thai Baht", symbol:"฿" },
  { code:"MYR", name:"Malaysian Ringgit", symbol:"RM" },
  { code:"IDR", name:"Indonesian Rupiah", symbol:"Rp" },
  { code:"KRW", name:"South Korean Won", symbol:"₩" },
  { code:"TWD", name:"Taiwan Dollar", symbol:"NT$" },
  { code:"PKR", name:"Pakistani Rupee", symbol:"₨" },
  { code:"BDT", name:"Bangladeshi Taka", symbol:"৳" },
  { code:"CLP", name:"Chilean Peso", symbol:"CLP$" },
  { code:"COP", name:"Colombian Peso", symbol:"COP$" },
  { code:"ARS", name:"Argentine Peso", symbol:"AR$" },
  { code:"PEN", name:"Peruvian Sol", symbol:"S/." },
  { code:"UAH", name:"Ukrainian Hryvnia", symbol:"₴" },
  { code:"VND", name:"Vietnamese Dong", symbol:"₫" },
];

/* ─────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;0,9..144,900;1,9..144,400;1,9..144,500&family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; height: 100%; }
body {
  background: ${C.bg}; color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100%; overflow-x: hidden; overflow-y: auto;
}
a { text-decoration: none; color: inherit; }

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes gridFade { from { opacity: 0; } to { opacity: .3; } }
@keyframes pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%     { opacity:.5; transform:scale(.9); }
}
@keyframes spinDot { to { transform: rotate(360deg); } }
@keyframes successPop {
  0%   { transform: scale(.8); opacity:0; }
  60%  { transform: scale(1.08); }
  100% { transform: scale(1); opacity:1; }
}
@keyframes shake {
  0%,100% { transform: translateX(0); }
  20%     { transform: translateX(-6px); }
  40%     { transform: translateX(6px); }
  60%     { transform: translateX(-4px); }
  80%     { transform: translateX(4px); }
}

.auth-card-enter   { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) .05s both; }
.auth-header-enter { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) .15s both; }

.grid-bg {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(${C.border} 1px, transparent 1px),
    linear-gradient(90deg, ${C.border} 1px, transparent 1px);
  background-size: 56px 56px;
  animation: gridFade 1.2s ease-out both;
  mask-image: radial-gradient(ellipse 70% 70% at 50% 40%, black 0%, transparent 100%);
}

.tf-nav {
  height: 60px;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 clamp(16px, 4vw, 40px);
  background: ${C.bg}F2; backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid ${C.border};
  position: sticky; top: 0; z-index: 100;
}

/* ── iOS PWA safe area (notch / Dynamic Island) ── */
@supports (padding-top: env(safe-area-inset-top)) {
  .tf-nav {
    padding-top: env(safe-area-inset-top);
    height: calc(60px + env(safe-area-inset-top));
  }
  .tf-nav-offset {
    min-height: calc(100vh - 60px - env(safe-area-inset-top));
  }
}

.tf-input {
  width: 100%; background: ${C.surfaceAlt};
  border: 1px solid ${C.border}; border-radius: 10px;
  padding: 13px 16px; font-size: 14px; color: ${C.text};
  font-family: 'Public Sans', sans-serif; outline: none;
  transition: border-color .2s, box-shadow .2s;
}
.tf-input:focus { border-color: ${C.accent}60; box-shadow: 0 0 0 3px ${C.accentDim}; }
.tf-input::placeholder { color: ${C.textDim}; }
.tf-input.error { border-color: ${C.red}70; box-shadow: 0 0 0 3px rgba(255,77,106,.08); }
.tf-input.success { border-color: ${C.accent}60; }

.tf-select {
  width: 100%; background: ${C.surfaceAlt};
  border: 1px solid ${C.border}; border-radius: 10px;
  padding: 13px 16px; font-size: 14px; color: ${C.text};
  font-family: 'Public Sans', sans-serif; outline: none;
  appearance: none; cursor: pointer;
  transition: border-color .2s, box-shadow .2s;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238C9BAC' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 14px center; padding-right: 38px;
}
.tf-select:focus { border-color: ${C.accent}60; box-shadow: 0 0 0 3px ${C.accentDim}; }
.tf-select option { background: ${C.surface}; color: ${C.text}; }
.tf-select.error { border-color: ${C.red}70; box-shadow: 0 0 0 3px rgba(255,77,106,.08); }
.tf-select.success { border-color: ${C.accent}60; }

.pw-wrap { position: relative; }
.pw-wrap .tf-input { padding-right: 46px; }
.pw-toggle {
  position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: ${C.textDim}; cursor: pointer;
  font-size: 17px; padding: 4px; line-height: 1; transition: color .15s;
}
.pw-toggle:hover { color: ${C.textMid}; }

.tf-checkbox {
  appearance: none; -webkit-appearance: none;
  width: 16px; height: 16px; border-radius: 4px;
  border: 1px solid ${C.border}; background: ${C.surfaceAlt};
  cursor: pointer; flex-shrink: 0;
  transition: border-color .15s, background .15s; position: relative;
}
.tf-checkbox:checked { background: ${C.accent}; border-color: ${C.accent}; }
.tf-checkbox:checked::after {
  content: '\u2713'; position: absolute; top: 50%; left: 50%;
  transform: translate(-50%,-52%); font-size: 10px; color: #F3FAF7; font-weight: 800;
}

.btn-primary {
  width: 100%; padding: 14px;
  background: ${C.accent}; color: #F3FAF7;
  border: none; border-radius: 10px;
  font-size: 14px; font-weight: 700;
  font-family: 'Public Sans', sans-serif; cursor: pointer; letter-spacing: .01em;
  transition: transform .18s, box-shadow .18s, background .18s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  position: relative; overflow: hidden;
}
.btn-primary::after {
  content:''; position:absolute; inset:0;
  background: linear-gradient(135deg,rgba(255,255,255,.16) 0%,transparent 60%);
  opacity:0; transition:opacity .2s;
}
.btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 10px 36px ${C.accentGlow}; }
.btn-primary:hover::after { opacity:1; }
.btn-primary:disabled { opacity:.55; cursor:not-allowed; transform:none; }

.step-dots { display:flex; align-items:center; gap:6px; justify-content:center; }
.step-dot { width:7px; height:7px; border-radius:50%; background:${C.border}; transition:background .25s, transform .25s; }
.step-dot.active { background:${C.accent}; transform:scale(1.3); }
.step-dot.done   { background:${C.accent}40; }

.field-error { font-size:12px; color:${C.red}; margin-top:5px; display:flex; align-items:center; gap:4px; }

.success-icon {
  width:64px; height:64px; border-radius:50%;
  background:${C.accentDim}; border:1px solid ${C.accent}40;
  display:flex; align-items:center; justify-content:center;
  font-size:28px; margin:0 auto 20px;
  animation: successPop .5s cubic-bezier(.22,1,.36,1) both;
}

.spinner {
  width:18px; height:18px; border-radius:50%;
  border:2px solid rgba(255,255,255,.3); border-top-color:#F3FAF7;
  animation: spinDot .7s linear infinite; flex-shrink:0;
}

.strength-bar { display:flex; gap:4px; margin-top:8px; }
.strength-seg { flex:1; height:3px; border-radius:2px; background:${C.border}; transition:background .3s; }

.badge-live {
  display:inline-flex; align-items:center; gap:5px;
  background:rgba(14,124,90,.08); border:1px solid rgba(14,124,90,.2);
  border-radius:6px; padding:3px 8px;
  font-family:'IBM Plex Mono',monospace; font-size:10px; color:${C.accent};
}
.badge-live::before {
  content:''; width:5px; height:5px; border-radius:50%;
  background:${C.accent}; animation:pulse 1.5s ease-in-out infinite; flex-shrink:0;
}

.form-two-col { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
@media(max-width:420px) { .form-two-col { grid-template-columns:1fr; } }

.phone-row { display:grid; grid-template-columns:110px 1fr; gap:10px; }
@media(max-width:420px) { .phone-row { grid-template-columns:96px 1fr; } }

::-webkit-scrollbar { width:5px; }
::-webkit-scrollbar-track { background:${C.bg}; }
::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }

/* ── Google Translate banner suppression ── */
.goog-te-banner-frame, .goog-te-balloon-frame,
.goog-te-ftab-float, #goog-gt-tt { display:none !important; }
body { top:0 !important; }
#tf-translate-el { display:none !important; }

/* ── Custom language dropdown ── */
.tf-lang-wrap { position:relative; display:inline-flex; align-items:center; }
.tf-lang-btn {
  display:flex; align-items:center; gap:6px;
  background:${C.surfaceAlt}; border:1px solid ${C.border};
  border-radius:8px; padding:6px 11px;
  font-size:12px; font-weight:600; color:${C.textMid};
  font-family:'Public Sans',sans-serif; cursor:pointer;
  transition:border-color .2s, color .2s; white-space:nowrap; outline:none;
}
.tf-lang-btn:hover, .tf-lang-btn.open { border-color:${C.borderHover}; color:${C.text}; }
.tf-lang-btn .tf-chevron { width:11px; height:11px; margin-left:2px; transition:transform .2s; opacity:.6; }
.tf-lang-btn.open .tf-chevron { transform:rotate(180deg); }
.tf-lang-menu {
  position:absolute; top:calc(100% + 6px); right:0;
  background:${C.surface}; border:1px solid ${C.border};
  border-radius:10px; padding:5px; min-width:170px; z-index:999;
  box-shadow:0 16px 40px rgba(22,40,63,.16); animation:fadeUp .15s ease both;
  max-height:280px; overflow-y:auto;
}
.tf-lang-opt {
  display:flex; align-items:center; gap:9px; padding:8px 10px; border-radius:7px;
  font-size:12px; color:${C.textMid}; cursor:pointer;
  font-family:'Public Sans',sans-serif; font-weight:500;
  transition:background .15s, color .15s;
}
.tf-lang-opt:hover { background:${C.surfaceAlt}; color:${C.text}; }
.tf-lang-opt.active { color:${C.accent}; background:${C.accentDim}; }
.tf-lang-opt .tf-lang-flag { font-size:15px; line-height:1; flex-shrink:0; }

@media (max-width: 767px) { .side-panel-desktop { display: none !important; } }
@media (max-width: 480px) { .auth-panel { padding: 20px 16px !important; } }
@media (max-width: 360px) { .auth-panel { padding: 16px 12px !important; } }
@media (min-width: 481px) and (max-width: 767px) { .auth-panel { padding: 28px 32px !important; } }
@media (min-width: 768px) and (max-width: 1024px) { .side-panel-desktop { max-width: 340px !important; } }
`;

/* ─────────────────────────────────────────────
   ICON HELPERS
───────────────────────────────────────────── */
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
/* ─────────────────────────────────────────────
   FIELD with label + error
───────────────────────────────────────────── */
const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: "20px" }}>
    <div style={{ marginBottom:"7px" }}>
      <label style={{ fontSize:"13px", fontWeight:"600", color:C.textMid, letterSpacing:".01em" }}>{label}</label>
    </div>
    {children}
    {error && <div className="field-error">&#9888; {error}</div>}
  </div>
);

/* ─────────────────────────────────────────────
   PASSWORD STRENGTH
───────────────────────────────────────────── */
const getStrength = (pw) => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
};
const strengthColor = (score) => [C.textDim,C.red,"#D68910",C.blue,C.accent][score];
const strengthLabel = (score) => ["","Weak","Fair","Good","Strong"][score];

const StrengthBar = ({ password }) => {
  if (!password) return null;
  const score = getStrength(password);
  return (
    <div>
      <div className="strength-bar">
        {[1,2,3,4].map(i => (
          <div key={i} className="strength-seg" style={{ background: i <= score ? strengthColor(score) : C.border }}/>
        ))}
      </div>
      {score > 0 && (
        <div style={{ fontSize:"11px", color:strengthColor(score), marginTop:"5px", fontFamily:"'IBM Plex Mono',monospace", letterSpacing:".04em" }}>
          {strengthLabel(score)}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   SIDE PANEL
───────────────────────────────────────────── */
const SidePanel = () => {
  const perks = [
    { icon:"📈", title:"Copy top traders",    desc:"Mirror verified strategies automatically." },
    { icon:"🤖", title:"AI-powered signals",  desc:"Real-time alerts based on live market data." },
    { icon:"🔒", title:"Bank-grade security", desc:"2FA, cold storage, and insured assets." },
  ];
  return (
    <div style={{
      position:"relative", flex:"1",
      background:`linear-gradient(160deg, ${C.surface} 0%, ${C.surfaceAlt} 100%)`,
      borderRight:`1px solid ${C.border}`,
      padding:"48px 36px",
      display:"flex", flexDirection:"column", justifyContent:"space-between",
      overflow:"hidden", minHeight:"calc(100vh - 60px - env(safe-area-inset-top, 0px))",
    }}>
      <div style={{position:"absolute", width:"380px", height:"380px", borderRadius:"50%", background:C.accentGlow, filter:"blur(90px)", top:"-100px", left:"-80px", pointerEvents:"none"}}/>
      <div style={{position:"absolute", width:"200px", height:"200px", borderRadius:"50%", background:"rgba(47,102,144,.12)", filter:"blur(60px)", bottom:"80px", right:"-40px", pointerEvents:"none"}}/>
      <div style={{position:"relative", zIndex:1}}>
        <a href="/" style={{display:"inline-flex", alignItems:"center", marginBottom:"40px"}}>
          <img src={logo} alt="Optionstock" style={{height:"38px", width:"auto"}} />
        </a>
        <div style={{marginBottom:"12px"}}>
          <div className="badge-live" style={{marginBottom:"16px"}}>280,000+ traders</div>
          <h2 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(22px,2.5vw,30px)", fontWeight:"800", letterSpacing:"-1px", color:C.text, lineHeight:"1.2", marginBottom:"12px"}}>
            Start trading<br/>smarter today.
          </h2>
          <p style={{fontSize:"14px", color:C.textMid, lineHeight:"1.7", maxWidth:"280px"}}>
            Create your free account in under 2 minutes and get access to the world's best copy trading platform.
          </p>
        </div>
      </div>
      <div style={{position:"relative", zIndex:1, display:"flex", flexDirection:"column", gap:"12px"}}>
        {perks.map((p,i) => (
          <div key={i} style={{
            background:"rgba(255,255,255,.75)", border:`1px solid ${C.border}`,
            borderRadius:"12px", padding:"16px 18px",
            display:"flex", gap:"14px", alignItems:"flex-start",
            backdropFilter:"blur(8px)",
            animation:`fadeUp .6s cubic-bezier(.22,1,.36,1) ${.4+i*.12}s both`,
          }}>
            <span style={{fontSize:"20px", flexShrink:0, marginTop:"1px"}}>{p.icon}</span>
            <div>
              <div style={{fontSize:"13px", fontWeight:"700", color:C.text, marginBottom:"3px"}}>{p.title}</div>
              <div style={{fontSize:"12px", color:C.textDim, lineHeight:"1.5"}}>{p.desc}</div>
            </div>
          </div>
        ))}
        <p style={{fontSize:"11px", color:C.textDim, textAlign:"center", marginTop:"6px"}}>
          Simulated data for illustration only
        </p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP 1 — Account details
───────────────────────────────────────────── */
const StepAccount = ({ onNext }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [email,     setEmail]     = useState("");
  const [password,  setPassword]  = useState("");
  const [show,      setShow]      = useState(false);
  const [errors,    setErrors]    = useState({});
  const [loading,   setLoading]   = useState(false);

  const validate = () => {
    const e = {};
    if (!firstName.trim())         e.firstName = "First name is required";
    if (!lastName.trim())          e.lastName  = "Last name is required";
    if (!email.includes("@"))      e.email     = "Enter a valid email address";
    if (getStrength(password) < 2) e.password  = "Password is too weak";
    return e;
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({}); setLoading(true);

    try {
      // Create the auth user directly — no OTP / email confirmation involved.
      const { data: authData, error: authErr } = await supabase.auth.signUp({
        email:    email.toLowerCase().trim(),
        password,
        options:  { data: { first_name: firstName.trim(), last_name: lastName.trim() } },
      });
      if (authErr) throw authErr;

      // A user object with empty identities means the email is already taken.
      if (authData?.user?.identities?.length === 0) {
        throw new Error("An account with this email already exists.");
      }

      const userId = authData?.user?.id;
      if (userId) {
        const { error: dbErr } = await supabase.from("users").upsert({
          id:            userId,
          email:         email.toLowerCase().trim(),
          password_hash: "managed_by_supabase_auth",
          first_name:    firstName.trim(),
          last_name:     lastName.trim(),
        }, { onConflict: "id" });
        if (dbErr) console.error("users upsert:", dbErr.message);
      }

      setLoading(false);
      onNext({ firstName, lastName, email });
    } catch (err) {
      setLoading(false);
      setErrors({ email: err.message || "Sign-up failed. Please try again." });
    }
  };

  return (
    <div>
      <div className="form-two-col">
        <Field label="First name" error={errors.firstName}>
          <input className={`tf-input${errors.firstName?" error":firstName.trim()?" success":""}`}
            type="text" placeholder="Alex" value={firstName}
            onChange={e=>{setFirstName(e.target.value);setErrors(p=>({...p,firstName:""}));}}
            autoComplete="given-name"/>
        </Field>
        <Field label="Last name" error={errors.lastName}>
          <input className={`tf-input${errors.lastName?" error":lastName.trim()?" success":""}`}
            type="text" placeholder="Kim" value={lastName}
            onChange={e=>{setLastName(e.target.value);setErrors(p=>({...p,lastName:""}));}}
            autoComplete="family-name"/>
        </Field>
      </div>
      <Field label="Email address" error={errors.email}>
        <input className={`tf-input${errors.email?" error":email.includes("@")?" success":""}`}
          type="email" placeholder="alex@email.com" value={email}
          onChange={e=>{setEmail(e.target.value);setErrors(p=>({...p,email:""}));}}
          onKeyDown={e=>e.key==="Enter"&&submit()} autoComplete="email"/>
      </Field>
      <Field label="Password" error={errors.password}>
        <div className="pw-wrap">
          <input className={`tf-input${errors.password?" error":""}`}
            type={show?"text":"password"} placeholder="Min. 8 characters" value={password}
            onChange={e=>{setPassword(e.target.value);setErrors(p=>({...p,password:""}));}}
            onKeyDown={e=>e.key==="Enter"&&submit()} autoComplete="new-password"/>
          <button type="button" className="pw-toggle" onClick={()=>setShow(s=>!s)}>
            {show?<EyeOffIcon/>:<EyeIcon/>}
          </button>
        </div>
        <StrengthBar password={password}/>
      </Field>
      <button className="btn-primary" onClick={submit} disabled={loading}>
        {loading?<><div className="spinner"/>Creating account…</>:<>Continue <span style={{fontSize:"16px"}}>&#8594;</span></>}
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP 2 — Profile preferences
───────────────────────────────────────────── */
const StepProfile = ({ onNext, onBack }) => {
  const [countryCode, setCountryCode] = useState("");
  const [phone,       setPhone]       = useState("");
  const [currency,    setCurrency]    = useState("");
  const [goal,        setGoal]        = useState("");
  const [terms,       setTerms]       = useState(false);
  const [errors,      setErrors]      = useState({});
  const [loading,     setLoading]     = useState(false);

  const handleCountryChange = (val) => {
    setCountryCode(val);
    setErrors(p=>({...p,country:""}));
    if (val && !currency) {
      const c = COUNTRIES.find(c=>c.code===val);
      if (c) setCurrency(c.currency);
    }
  };

  const selectedCountry = COUNTRIES.find(c=>c.code===countryCode);

  const validate = () => {
    const e = {};
    if (!countryCode)  e.country  = "Please select your country";
    if (!phone.trim()) e.phone    = "Phone number is required";
    if (!currency)     e.currency = "Please select your currency";
    if (!goal)         e.goal     = "Please select your trading goal";
    if (!terms)        e.terms    = "You must accept the terms to continue";
    return e;
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({}); setLoading(true);

    try {
      const { data: { user }, error: sessionErr } = await supabase.auth.getUser();
      if (sessionErr || !user) throw new Error("Session expired. Please sign in again.");

      const country = COUNTRIES.find(c => c.code === countryCode);

      const { error: dbErr } = await supabase.from("users").update({
        country_code:  countryCode,
        country_name:  country?.name ?? null,
        currency:      currency      || null,
        dial_code:     country?.dial ?? null,
        phone:         phone.trim()  || null,
        trading_goal:  goal          || null,
        updated_at:    new Date().toISOString(),
      }).eq("id", user.id);
      if (dbErr) throw dbErr;

      setLoading(false);
      onNext();
    } catch (err) {
      setLoading(false);
      setErrors({ country: err.message || "Could not save your profile. Please try again." });
    }
  };

  return (
    <div>
      {/* Country */}
      <Field label="Country" error={errors.country}>
        <select className={`tf-select${errors.country?" error":countryCode?" success":""}`}
          value={countryCode} onChange={e=>handleCountryChange(e.target.value)}>
          <option value="">Select your country…</option>
          {COUNTRIES.map(c=>(
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
      </Field>

      {/* Phone */}
      <Field label="Phone number" error={errors.phone}>
        <div className="phone-row">
          <div style={{
            background:C.surfaceAlt, border:`1px solid ${C.border}`,
            borderRadius:"10px", padding:"13px 10px",
            fontSize:"13px", color:selectedCountry?C.textMid:C.textDim,
            fontFamily:"'IBM Plex Mono',monospace",
            display:"flex", alignItems:"center", whiteSpace:"nowrap",
          }}>
            {selectedCountry ? selectedCountry.dial : "+???"}
          </div>
          <input
            className={`tf-input${errors.phone?" error":phone.trim()?" success":""}`}
            type="tel" placeholder="800 000 0000"
            value={phone}
            onChange={e=>{setPhone(e.target.value);setErrors(p=>({...p,phone:""}));}}
            autoComplete="tel-national"
          />
        </div>
      </Field>

      {/* Currency */}
      <Field label="Preferred currency" error={errors.currency}>
        <select className={`tf-select${errors.currency?" error":currency?" success":""}`}
          value={currency} onChange={e=>{setCurrency(e.target.value);setErrors(p=>({...p,currency:""}));}}>
          <option value="">Select currency…</option>
          {CURRENCIES.map(c=>(
            <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
          ))}
        </select>
      </Field>

      {/* Goal */}
      <Field label="I want to" error={errors.goal}>
        <select className={`tf-select${errors.goal?" error":goal?" success":""}`}
          value={goal} onChange={e=>{setGoal(e.target.value);setErrors(p=>({...p,goal:""}));}}>
          <option value="">Select your goal…</option>
          <option value="copy">Copy successful traders</option>
          <option value="hire">Hire a professional trader</option>
          <option value="independent">Trade independently</option>
          <option value="all">All of the above</option>
        </select>
      </Field>

      {/* Terms */}
      <div style={{marginBottom:"24px"}}>
        <div style={{display:"flex", alignItems:"flex-start", gap:"10px"}}>
          <input type="checkbox" className="tf-checkbox" id="terms"
            checked={terms} onChange={e=>{setTerms(e.target.checked);setErrors(p=>({...p,terms:""}));}}
            style={{marginTop:"2px"}}/>
          <label htmlFor="terms" style={{fontSize:"13px", color:C.textMid, cursor:"pointer", lineHeight:"1.6", userSelect:"none"}}>
            I agree to the{" "}
            <a href="/terms" style={{color:C.accent, fontWeight:"600"}}>Terms of Service</a>
            {" "}and{" "}
            <a href="/privacy" style={{color:C.accent, fontWeight:"600"}}>Privacy Policy</a>.
            {" "}I confirm I am 18+ years of age.
          </label>
        </div>
        {errors.terms && <div className="field-error" style={{marginTop:"8px"}}>&#9888; {errors.terms}</div>}
      </div>

      <button className="btn-primary" onClick={submit} disabled={loading}>
        {loading?<><div className="spinner"/>Setting up…</>:<>Create Account <span style={{fontSize:"16px"}}>&#8594;</span></>}
      </button>

      <button onClick={onBack} style={{
        width:"100%", marginTop:"12px", padding:"11px",
        background:"transparent", border:`1px solid ${C.border}`, borderRadius:"10px",
        color:C.textMid, fontSize:"13px", fontWeight:"600", fontFamily:"'Public Sans',sans-serif",
        cursor:"pointer", transition:"border-color .2s, color .2s",
      }}
        onMouseEnter={e=>{e.currentTarget.style.borderColor=C.borderHover;e.currentTarget.style.color=C.text;}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.textMid;}}
      >&#8592; Back</button>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STEP 3 — Success
───────────────────────────────────────────── */
const StepSuccess = ({ name }) => {
  useEffect(() => {
    const t = setTimeout(() => { window.location.href = "/dashboard"; }, 2500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{textAlign:"center", padding:"12px 0 8px"}}>
      <div className="success-icon">&#10003;</div>
      <h3 style={{fontFamily:"'Fraunces',serif", fontSize:"20px", fontWeight:"800", color:C.text, marginBottom:"8px"}}>
        Welcome, {name}!
      </h3>
      <p style={{fontSize:"14px", color:C.textMid, lineHeight:"1.65", marginBottom:"24px"}}>
        Your account is ready. Redirecting you to your dashboard…
      </p>
      <div style={{display:"flex", justifyContent:"center"}}>
        <div style={{
          width:"36px", height:"36px", borderRadius:"50%",
          border:`2px solid ${C.border}`, borderTopColor:C.accent,
          animation:"spinDot .8s linear infinite",
        }}/>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN SIGNUP PAGE
───────────────────────────────────────────── */
const SignUp = () => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({});

  // Inject & persist Google Translate
  useGoogleTranslate();

  // Inject Chatway live-chat widget
  useChatwayWidget();

  const totalSteps = 2;

  const goStep2   = (info) => { setData(info); setStep(2); };
  const goSuccess = ()     => { setStep(3); };
  const goBack    = ()     => { setStep(s => s - 1); };

  const stepLabel = { 1:"Your details", 2:"Preferences", 3:"All set" };

  return (
    <>
      <style>{CSS}</style>

      <nav className="tf-nav">
        <a href="/" style={{display:"flex", alignItems:"center"}}>
          <img src={logo} alt="Optionstock" style={{height:"38px", width:"auto"}} />
        </a>
        <div style={{display:"flex", alignItems:"center", gap:"12px"}}>
          {/* Google Translate dropdown */}
          <LanguagePicker/>
          <span style={{fontSize:"13px", color:C.textDim}}>Already a member?</span>
          <a href="/login" style={{
            fontSize:"12px", padding:"8px 16px", borderRadius:"8px",
            background:C.accentDim, border:`1px solid ${C.accent}40`,
            color:C.accent, fontWeight:"700", letterSpacing:".01em",
            transition:"background .2s, color .2s",
          }}>Log in &#8594;</a>
        </div>
      </nav>

      <div className="grid-bg"/>

      <div className="tf-nav-offset" style={{
        minHeight:"calc(100vh - 60px)", display:"flex",
        position:"relative", zIndex:1, alignItems:"stretch",
      }}>
        <div className="side-panel-desktop" style={{display:"none", flex:"1", maxWidth:"480px"}}>
          <SidePanel/>
        </div>
        <style>{`@media(min-width:768px){ .side-panel-desktop{ display:flex !important; } }`}</style>

        <div style={{
          flex:"1", display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:"clamp(32px,6vw,64px) clamp(16px,5vw,48px)",
        }}>
          <div style={{width:"100%", maxWidth:"400px"}}>

            <div className="auth-header-enter" style={{marginBottom:"32px"}}>
              {step < 3 && (
                <div style={{marginBottom:"20px"}}>
                  <div className="step-dots">
                    {Array.from({length:totalSteps},(_,i)=>(
                      <div key={i} className={`step-dot ${i+1===step?"active":i+1<step?"done":""}`}/>
                    ))}
                  </div>
                  <div style={{textAlign:"center", fontSize:"11px", color:C.textDim, marginTop:"8px", fontFamily:"'IBM Plex Mono',monospace", letterSpacing:".06em", textTransform:"uppercase"}}>
                    Step {step} of {totalSteps} — {stepLabel[step]}
                  </div>
                </div>
              )}
              <h1 style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(24px,3vw,30px)", fontWeight:"800", letterSpacing:"-1px", color:C.text, marginBottom:"6px", textAlign:"center"}}>
                {step===1?"Create your account":step===2?"Almost there":"You're in!"}
              </h1>
              <p style={{fontSize:"14px", color:C.textMid, textAlign:"center", lineHeight:"1.6"}}>
                {step===1?"Join 280,000+ traders worldwide":
                 step===2?"A few more details to personalize your experience":""}
              </p>
            </div>

            <div className="auth-card-enter" style={{
              background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:"18px", padding:"clamp(20px,4vw,32px)",
              boxShadow:`0 32px 64px rgba(22,40,63,.14), 0 0 0 1px ${C.border}`,
            }}>
              {step===1 && <StepAccount onNext={goStep2}/>}
              {step===2 && <StepProfile onNext={goSuccess} onBack={goBack}/>}
              {step===3 && <StepSuccess name={data.firstName || "Trader"}/>}
            </div>

            {step < 3 && (
              <div style={{textAlign:"center", marginTop:"24px", fontSize:"13px", color:C.textDim}}>
                Already have an account?{" "}
                <a href="/login" style={{color:C.accent, fontWeight:"600"}}>Sign in &#8594;</a>
              </div>
            )}

            <p style={{textAlign:"center", fontSize:"11px", color:C.textDim, marginTop:"20px", lineHeight:"1.6"}}>
              By creating an account you agree to our{" "}
              <a href="/terms" style={{color:C.textDim, textDecoration:"underline"}}>Terms</a>
              {" "}and{" "}
              <a href="/privacy" style={{color:C.textDim, textDecoration:"underline"}}>Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;